import React, { useRef, useState } from 'react';
import { X, Loader2, CheckCircle2, PlusCircle, MinusCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ImportDiff, ImportDiffItem } from '../api';

interface Props {
  diff: ImportDiff;
  onClose: () => void;
  onConfirm: () => void;
  confirming: boolean;
}

const TYPE_META: Record<ImportDiffItem['type'], { label: string; color: string; icon: any }> = {
  add: { label: 'Aggiunto', color: 'text-[#2f8a4f] bg-[#eefaf1]', icon: PlusCircle },
  update: { label: 'Modificato', color: 'text-[#9a6b12] bg-[#fdf8ec]', icon: RefreshCw },
  remove: { label: 'Rimosso', color: 'text-[#8a3b3b] bg-[#fdf5f5]', icon: MinusCircle },
};

const ENTITY_LABEL: Record<ImportDiffItem['entity'], string> = {
  machine: 'Macchina',
  rebrand: 'Rebrand',
  association: 'Abbinamento',
};

/** Modale di riepilogo pre-import: mostra il diff calcolato in dry-run dal
 * backend (nessuna scrittura su DB) prima di confermare l'importazione
 * effettiva. La lista dettagliata è virtualizzata (@tanstack/react-virtual)
 * per reggere migliaia di righe senza rallentare il DOM. */
export default function ImportDiffModal({ diff, onClose, onConfirm, confirming }: Props) {
  const [filter, setFilter] = useState<'all' | 'add' | 'update' | 'remove'>('all');
  const parentRef = useRef<HTMLDivElement>(null);

  const filteredItems = filter === 'all' ? diff.items : diff.items.filter(i => i.type === filter);

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 42,
    overscan: 12,
  });

  const hasDestructive = diff.machines_to_remove > 0 || diff.associations_to_remove > 0;
  const noChanges = diff.machines_to_create === 0 && diff.rebrand_to_set === 0
    && diff.associations_to_create === 0 && diff.machines_to_remove === 0 && diff.associations_to_remove === 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="import-diff-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget && !confirming) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl h-[86vh] flex flex-col" data-testid="import-diff-modal">
        {/* Header */}
        <div className="flex-none px-6 py-4 border-b border-[#e4e4e1] flex items-center gap-3">
          <RefreshCw className="w-4 h-4 text-[#2b3a8f] flex-none" />
          <h2 className="text-[17px] font-semibold text-[#1f1f1d] flex-1">
            Riepilogo modifiche — {diff.mode === 'replace' ? 'Sostituisci tutto' : 'Unisci'}
          </h2>
          <button
            onClick={onClose}
            disabled={confirming}
            data-testid="import-diff-close"
            className="text-[#5c5c57] hover:text-[#3a3a36] transition-colors disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stat cards */}
        <div className="flex-none grid grid-cols-3 sm:grid-cols-6 gap-2.5 px-6 py-4 border-b border-[#eee]" data-testid="import-diff-stats">
          <DiffStat label="Macchine nuove" value={diff.machines_to_create} color="text-[#2f8a4f]" />
          <DiffStat label="Macchine invariate" value={diff.machines_matched} color="text-[#5c5c57]" />
          <DiffStat label="Macchine rimosse" value={diff.machines_to_remove} color="text-[#8a3b3b]" />
          <DiffStat label="Rebrand aggiornati" value={diff.rebrand_to_set} color="text-[#9a6b12]" />
          <DiffStat label="Abbinamenti nuovi" value={diff.associations_to_create} color="text-[#2f8a4f]" />
          <DiffStat label="Abbinamenti rimossi" value={diff.associations_to_remove} color="text-[#8a3b3b]" />
        </div>

        {hasDestructive && (
          <div className="flex-none px-6 py-2.5 bg-[#fdf5f5] border-b border-[#f3d5d5] flex items-center gap-2 text-[13px] text-[#8a3b3b]" data-testid="import-diff-destructive-warning">
            <AlertTriangle className="w-3.5 h-3.5 flex-none" />
            Questa operazione rimuoverà definitivamente {diff.machines_to_remove.toLocaleString('it')} macchine e{' '}
            {diff.associations_to_remove.toLocaleString('it')} abbinamenti non presenti nel file.
          </div>
        )}

        {/* Filter tabs */}
        {!noChanges && (
          <div className="flex-none flex items-center gap-1.5 px-6 pt-3" data-testid="import-diff-filters">
            {(['all', 'add', 'update', 'remove'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`import-diff-filter-${f}`}
                className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors ${
                  filter === f ? 'bg-[#2b3a8f] text-white' : 'bg-[#f3f3f0] text-[#5c5c57] hover:bg-[#e9e9e5]'
                }`}
              >
                {f === 'all' ? `Tutte (${diff.items.length})`
                  : f === 'add' ? 'Aggiunte' : f === 'update' ? 'Modifiche' : 'Rimozioni'}
              </button>
            ))}
          </div>
        )}

        {/* Virtualized list */}
        <div ref={parentRef} className="flex-1 overflow-y-auto px-6 py-3 min-h-0" data-testid="import-diff-list">
          {noChanges ? (
            <div className="text-center text-[14px] text-[#8a8a85] py-10" data-testid="import-diff-no-changes">
              Nessuna modifica da applicare: il file coincide con il catalogo attuale.
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center text-[14px] text-[#8a8a85] py-10">Nessuna riga in questa categoria.</div>
          ) : (
            <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
              {virtualizer.getVirtualItems().map(vi => {
                const item = filteredItems[vi.index];
                const meta = TYPE_META[item.type];
                const Icon = meta.icon;
                return (
                  <div
                    key={vi.key}
                    data-testid={`import-diff-row-${vi.index}`}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: vi.size, transform: `translateY(${vi.start}px)` }}
                    className="flex items-center gap-3 px-2 border-b border-[#f0f0ed]"
                  >
                    <span className={`flex-none inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className="flex-none text-[11px] text-[#8a8a85] w-[84px]">{ENTITY_LABEL[item.entity]}</span>
                    <span className="flex-1 text-[13.5px] text-[#1f1f1d] truncate">{item.label}</span>
                    {item.detail && (
                      <span className="flex-none text-[12.5px] text-[#5c5c57] truncate max-w-[260px]">{item.detail}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {diff.items_truncated && (
            <div className="text-center text-[12.5px] text-[#8a8a85] py-3" data-testid="import-diff-truncated-note">
              Elenco troncato: mostrate {diff.items.length.toLocaleString('it')} di {diff.items_total.toLocaleString('it')} modifiche totali
              (i conteggi sopra sono comunque esatti).
            </div>
          )}
        </div>

        {diff.errors_total > 0 && (
          <div className="flex-none px-6 py-2 border-t border-[#eee] text-[12.5px] text-[#8a3b3b]" data-testid="import-diff-errors-note">
            {diff.errors_total} righe del file ignorate per errori di formato (verranno mostrate anche dopo l'import).
          </div>
        )}

        {/* Footer */}
        <div className="flex-none px-6 py-4 border-t border-[#e4e4e1] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={confirming}
            data-testid="import-diff-cancel"
            className="px-3.5 py-2 text-[14px] font-semibold text-[#5c5c57] hover:bg-[#fafafa] rounded-[7px] transition-colors disabled:opacity-40"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={confirming || noChanges}
            data-testid="import-diff-confirm"
            className={`px-3.5 py-2 rounded-[7px] text-white text-[14px] font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40 ${
              diff.mode === 'replace' ? 'bg-[#8a3b3b] hover:bg-[#712f2f]' : 'bg-[#2b3a8f] hover:bg-[#243279]'
            }`}
          >
            {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {confirming ? 'Importazione in corso…' : 'Conferma e importa'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DiffStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-[#f7f7f5] rounded-[9px] px-2.5 py-2 text-center">
      <div className={`text-[17px] font-semibold leading-none ${color}`}>{value.toLocaleString('it')}</div>
      <div className="text-[10.5px] text-[#8a8a85] mt-1 truncate">{label}</div>
    </div>
  );
}
